import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import classnames from 'classnames'

import draftJs from 'draft-js'
const {
  Editor,
  EditorState,
  RichUtils,
  CompositeDecorator,
  convertFromRaw,
  convertToRaw
} = draftJs

import 'draft-js/dist/Draft.css'
import styles from './RecipeEditor.sass'
import './RecipeEditorGlobal.css'

import { Icon } from '../../ui/bootstrap/bootstrap'
import { ingredientsByName } from '../../models/utils'
import { IngredientAmount } from '../ingredients/IngredientAmount.jsx'
import { RecipeTime } from './RecipeTime.jsx'
import { RecipeTemperature } from './RecipeTemperature.jsx'

const timeRegExp = '(\\d+)\\s*(minutes|minute|minutos|minuto|hours'
  + '|hour|horas|hora|days|day|días|día|min|m|s|h|d)'
const tempRegExp = '(\\d+)\\s*º?\\s*([CcFf])'

const emptyRecipe = {
  entityMap: {},
  blocks: [{
    key: '1nuug',
    text: '',
    type: 'ordered-list-item',
    depth: 0,
    inlineStyleRanges: [],
    entityRanges: []
  }]
}

// Draft-js needs the decorated text to stay in the DOM (that's
// what props.children is): it maps browser edits back to its
// content model through those text nodes. Decorations that are
// not part of the text go in these non-editable islands.
const NonEditable = ({ children }) =>
  <span contentEditable={false}>
    {children}
  </span>

const IngredientTag = ({ entry, children }) =>
  <span className={styles.ingredient}>
    <NonEditable>
      <Icon icon="tag" iconClass={styles.icon} />
      {' '}
    </NonEditable>
    {children}
    {entry &&
      <NonEditable>
        <IngredientAmount
          entry={entry}
          className={styles.amount}
        />
      </NonEditable>}
  </span>

const HandleIngredient = props => {
  const byName = props.ingredientsByName
  const ingredient = byName[props.decoratedText.toLowerCase()]
  if (!ingredient)
    return props.children

  const ingredientList = props.meal.ingredientList || []
  const entry = ingredientList.find(cur =>
    cur.ingredient.id == ingredient.id
  )

  return (
    <IngredientTag entry={entry}>
      {props.children}
    </IngredientTag>
  )
}

// INGREDIENT entities carry the ingredient id, so they survive
// renames that would break the by-name fallback
const HandleIngredientEntity = props => {
  const { id } = props.contentState
    .getEntity(props.entityKey)
    .getData()

  const ingredientList = props.meal.ingredientList || []
  const entry = ingredientList.find(cur =>
    cur.ingredient.id == id
  )

  if (!entry)
    return props.children

  return (
    <IngredientTag entry={entry}>
      {props.children}
    </IngredientTag>
  )
}

const findIngredientEntities = (block, callback, contentState) => {
  block.findEntityRanges(char => {
    const key = char.getEntity()

    return key !== null
      && contentState.getEntity(key).getType() == 'INGREDIENT'
  }, callback)
}

const HandleTime = props =>
  <RecipeTime>{props.children}</RecipeTime>

const HandleTemp = props =>
  <RecipeTemperature>{props.children}</RecipeTemperature>

const escapeRegExp = str =>
  str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const letter = /\p{L}/u

// Decorates matches surrounded by non-letters. While editing, a
// match at the very end of the block does not count: the tag only
// appears once you type something after it, so it doesn't flicker
// while you are still typing a longer word. In read-only mode
// nobody is typing, so end-of-block matches decorate too (atEnd).
function findWithRegex(search, block, callback, atEnd) {
  const text = block.getText()
  const regex = new RegExp(search, 'gi')

  let match
  while ((match = regex.exec(text)) !== null) {
    if (match[0].length == 0) {
      regex.lastIndex++
      continue
    }

    const start = match.index
    const end = start + match[0].length
    const before = text[start - 1]
    const after = text[end]

    const freeLeft = before === undefined || !letter.test(before)
    const freeRight = after === undefined
      ? atEnd
      : !letter.test(after)
    if (freeLeft && freeRight)
      callback(start, end)
  }
}

export const RecipeEditor = forwardRef((props, ref) => {
  const { meal, readOnly, onSubmit } = props

  const byName = useMemo(() => {
    const ingredientList = meal.ingredientList || []
    const ingredients = ingredientList.map(entry => entry.ingredient)
    const keyed = ingredientsByName(ingredients)

    return Object.keys(keyed).reduce((hash, name) => {
      hash[name.toLowerCase()] = keyed[name]
      return hash
    }, {})
  }, [meal])

  const decorator = useMemo(() => {
    // Longest names first and no overlaps, so "sal" never breaks
    // a "sal gorda" decoration in two.
    const checkIngredient = (block, callback) => {
      const names = Object.keys(byName)
        .sort((a, b) => b.length - a.length)

      const taken = []
      const free = (start, end) =>
        taken.every(([s, e]) => end <= s || start >= e)

      names.forEach(name => {
        findWithRegex(escapeRegExp(name), block, (start, end) => {
          if (free(start, end)) {
            taken.push([start, end])
            callback(start, end)
          }
        }, readOnly)
      })
    }

    // Entities go first: earlier decorators win overlapping ranges,
    // so the by-name regex only acts as fallback for old recipes
    return new CompositeDecorator([
      {
        strategy: findIngredientEntities,
        component: HandleIngredientEntity,
        props: { meal }
      },
      {
        strategy: checkIngredient,
        component: HandleIngredient,
        props: { ingredientsByName: byName, meal }
      },
      {
        strategy: (block, callback) =>
          findWithRegex(timeRegExp, block, callback, readOnly),
        component: HandleTime
      },
      {
        strategy: (block, callback) =>
          findWithRegex(tempRegExp, block, callback, readOnly),
        component: HandleTemp
      }
    ])
  }, [byName, meal, readOnly])

  const [editorState, setEditorState] = useState(() => {
    const initial = convertFromRaw(props.initial || emptyRecipe)
    return EditorState.createWithContent(initial, decorator)
  })

  const editorRef = useRef(null)
  const focus = () => {
    if (editorRef.current)
      editorRef.current.focus()
  }

  const submit = () =>
    onSubmit(convertToRaw(editorState.getCurrentContent()))

  useImperativeHandle(ref, () => ({ submit }))

  const handleKeyCommand = command => {
    const newState = RichUtils.handleKeyCommand(editorState, command)
    if (newState) {
      setEditorState(newState)
      return 'handled'
    } else
      return 'not-handled'
  }

  const toggleBlockType = type =>
    setEditorState(RichUtils.toggleBlockType(editorState, type))

  const toggleInlineStyle = style =>
    setEditorState(RichUtils.toggleInlineStyle(editorState, style))

  return (
    <div className={styles.root}>
      {!readOnly &&
        <div>
          <BlockStyleControls
            editorState={editorState}
            onToggle={toggleBlockType}
          />
          <InlineStyleControls
            editorState={editorState}
            onToggle={toggleInlineStyle}
          />
        </div>}
      <div className="RichEditor-editor" onClick={focus}>
        <Editor
          blockStyleFn={getBlockStyle}
          customStyleMap={styleMap}
          editorState={editorState}
          handleKeyCommand={handleKeyCommand}
          onChange={setEditorState}
          readOnly={readOnly}
          ref={editorRef}
        />
      </div>
    </div>
  )
})

// Custom overrides for "code" style.
const styleMap = {
  CODE: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
    fontSize: 16,
    padding: 2
  }
}

function getBlockStyle(block) {
  switch (block.getType()) {
  case 'ordered-list-item':
    return 'recipe-line'
  case 'blockquote':
    return 'RichEditor-blockquote'
  default:
    return null
  }
}

const StyleButton = ({ active, label, style, onToggle }) => {
  const handleToggle = e => {
    e.preventDefault()
    onToggle(style)
  }

  const className = classnames('RichEditor-styleButton', {
    'RichEditor-activeButton': active
  })

  return (
    <span className={className} onMouseDown={handleToggle}>
      {label}
    </span>
  )
}

const BLOCK_TYPES = [
  { label: 'H1', style: 'header-one' },
  { label: 'H2', style: 'header-two' },
  { label: 'H3', style: 'header-three' },
  { label: 'H4', style: 'header-four' },
  { label: 'H5', style: 'header-five' },
  { label: 'H6', style: 'header-six' },
  { label: 'Blockquote', style: 'blockquote' },
  { label: 'UL', style: 'unordered-list-item' },
  { label: 'OL', style: 'ordered-list-item' },
  { label: 'Code Block', style: 'code-block' }
]

const BlockStyleControls = ({ editorState, onToggle }) => {
  const selection = editorState.getSelection()
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType()

  return (
    <div className="RichEditor-controls">
      {BLOCK_TYPES.map(type =>
        <StyleButton
          key={type.label}
          active={type.style === blockType}
          label={type.label}
          onToggle={onToggle}
          style={type.style}
        />
      )}
    </div>
  )
}

const INLINE_STYLES = [
  { label: 'Bold', style: 'BOLD' },
  { label: 'Italic', style: 'ITALIC' },
  { label: 'Underline', style: 'UNDERLINE' },
  { label: 'Monospace', style: 'CODE' }
]

const InlineStyleControls = ({ editorState, onToggle }) => {
  const currentStyle = editorState.getCurrentInlineStyle()

  return (
    <div className="RichEditor-controls">
      {INLINE_STYLES.map(type =>
        <StyleButton
          key={type.label}
          active={currentStyle.has(type.style)}
          label={type.label}
          onToggle={onToggle}
          style={type.style}
        />
      )}
    </div>
  )
}
